import React, { useCallback, useEffect, useState } from "react";
import { financeAPI } from "../services/api";
import { useI18n } from "../i18n";
import useShopCurrency from "../hooks/useShopCurrency";
import { formatMoney } from "../pos/utils/money";
import { formatDate, todayIso as today, monthStartIso as monthStart } from "../utils/dates";
import { useMembership } from "../context/MembershipContext";
import EmptyState from "../components/EmptyState";

/**
 * What the shop spends outside the cost of what it sells.
 *
 * Deliberately quick to file: an owner standing in his shop with a receipt in
 * his hand should be three fields from done, which is the difference between
 * this being used and abandoned in week two.
 */
const KNOWN_CATEGORIES = [
  "rent",
  "salary",
  "utilities",
  "supplies",
  "transport",
  "marketing",
  "equipment",
  "fees",
  "other",
];


const Expenses = () => {
  const { t } = useI18n();
  const { can, isOwner, moduleOn } = useMembership();
  const currency = useShopCurrency();
  const money = (value) => formatMoney(value, currency);
  const categoryLabel = (category) =>
    KNOWN_CATEGORIES.includes(category)
      ? t(`finance.expenses.categories.${category}`)
      : category;

  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [range, setRange] = useState({ start: monthStart(), end: today() });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [form, setForm] = useState({
    category: "supplies",
    label: "",
    amount: "",
    occurred_on: today(),
    supplier: "",
    is_recurring: false,
    notes: "",
    paid_from: "outside",
  });

  // Where the money came from. The savings are the owner's; the drawer needs
  // "manage cash" and a shop that uses one. Nothing to choose otherwise.
  const sources = [
    "outside",
    ...(isOwner ? ["safe"] : []),
    ...(can("power.manage_cash") && moduleOn("cash_drawer") ? ["drawer"] : []),
  ];

  const load = useCallback(async () => {
    try {
      const { data } = await financeAPI.expenses({
        start_date: range.start,
        end_date: range.end,
      });
      setExpenses(data.expenses);
      setCategories(data.categories);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || t("finance.expenses.loadFailed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");

    try {
      const payload = { ...form, amount: parseFloat(form.amount) };

      // Only pay the cost of a multipart request when there is a file
      if (receipt) {
        const body = new FormData();
        Object.entries(payload).forEach(([key, value]) =>
          body.append(key, value),
        );
        body.append("receipt", receipt);
        await financeAPI.createExpense(body);
      } else {
        await financeAPI.createExpense(payload);
      }

      setForm({ ...form, label: "", amount: "", notes: "" });
      setReceipt(null);

      // A rent (or other) expense is often filed ahead of when it's due —
      // the visible range defaults to "this month up to today", so a
      // future-dated one would silently fall outside it and look like the
      // save had failed. Stretch the range to cover it instead.
      const nextRange = {
        start: payload.occurred_on < range.start ? payload.occurred_on : range.start,
        end: payload.occurred_on > range.end ? payload.occurred_on : range.end,
      };
      if (nextRange.start !== range.start || nextRange.end !== range.end) {
        setRange(nextRange);
      } else {
        await load();
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          t("finance.expenses.saveFailed"),
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async (expense) => {
    try {
      await financeAPI.deleteExpense(expense.id);
      await load();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          t("finance.expenses.removeFailed"),
      );
    }
  };

  const total = expenses.reduce((sum, e) => sum + parseFloat(e.amount), 0);

  const byCategory = expenses.reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + parseFloat(e.amount);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          {t("finance.expenses.title")}
        </h1>
        <p className="text-gray-600">{t("finance.expenses.subtitle")}</p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {can("expenses.edit") && (
        <form onSubmit={submit} className="bg-white rounded-xl shadow-lg p-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("finance.expenses.what")}
              </span>
              <input
                type="text"
                required
                placeholder={t("finance.expenses.whatPlaceholder")}
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("finance.expenses.amount", { currency })}
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("finance.expenses.category")}
              </span>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {categoryLabel(category)}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("finance.expenses.when")}
              </span>
              <input
                type="date"
                required
                value={form.occurred_on}
                onChange={(e) =>
                  setForm({ ...form, occurred_on: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </label>
          </div>

          {sources.length > 1 && (
            <label className="mt-4 block sm:max-w-xs">
              <span className="text-sm font-medium text-gray-700">
                {t("finance.expenses.paidFrom")}
              </span>
              <select
                value={form.paid_from}
                onChange={(e) => setForm({ ...form, paid_from: e.target.value })}
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {sources.map((source) => (
                  <option key={source} value={source}>
                    {t(`finance.expenses.sources.${source}`)}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-gray-500">
                {t(`finance.expenses.sourceHints.${form.paid_from}`)}
              </span>
            </label>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.is_recurring}
                onChange={(e) =>
                  setForm({ ...form, is_recurring: e.target.checked })
                }
                className="h-4 w-4"
              />
              <span className="text-sm text-gray-700">
                {t("finance.expenses.monthly")}
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
              <input
                type="file"
                accept="image/*,application/pdf"
                capture="environment"
                onChange={(e) => setReceipt(e.target.files?.[0] || null)}
                className="hidden"
              />
              <span className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50">
                {receipt
                  ? t("finance.expenses.photoAttached")
                  : t("finance.expenses.attach")}
              </span>
            </label>

            {receipt && (
              <button
                type="button"
                onClick={() => setReceipt(null)}
                className="text-sm text-gray-500 hover:underline"
              >
                {t("common.remove")}
              </button>
            )}

            <button
              type="submit"
              disabled={busy}
              className="px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-60"
            >
              {busy ? t("common.saving") : t("finance.expenses.record")}
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-3">
            <label className="block">
              <span className="text-xs text-gray-500">{t("common.from")}</span>
              <input
                type="date"
                value={range.start}
                onChange={(e) => setRange({ ...range, start: e.target.value })}
                className="block mt-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs text-gray-500">{t("common.to")}</span>
              <input
                type="date"
                value={range.end}
                onChange={(e) => setRange({ ...range, end: e.target.value })}
                className="block mt-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              />
            </label>
          </div>

          <div className="text-end">
            <p className="text-xs uppercase tracking-wide text-gray-500">
              {t("finance.expenses.totalPeriod")}
            </p>
            <p className="text-2xl font-bold text-gray-900">{money(total)}</p>
          </div>
        </div>

        {Object.keys(byCategory).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {Object.entries(byCategory)
              .sort((a, b) => b[1] - a[1])
              .map(([category, amount]) => (
                <span
                  key={category}
                  className="text-sm px-3 py-1.5 rounded-full bg-gray-100 text-gray-700"
                >
                  {categoryLabel(category)}{" "}
                  <span className="font-semibold whitespace-nowrap">
                    {money(amount)}
                  </span>
                </span>
              ))}
          </div>
        )}

        {loading ? (
          <p className="text-gray-500 py-8 text-center">{t("common.loading")}</p>
        ) : expenses.length === 0 ? (
          <EmptyState screen="expenses" message={t("finance.expenses.nothingPeriod")} />
        ) : (
          <ul className="divide-y divide-gray-100">
            {expenses.map((expense) => (
              <li
                key={expense.id}
                className="py-3 flex flex-wrap items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">
                    {expense.label}
                    {expense.paid_from && expense.paid_from !== "outside" && (
                      <span className="ms-2 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">
                        {t(`finance.expenses.sourceTags.${expense.paid_from}`)}
                      </span>
                    )}
                    {expense.is_recurring && (
                      <span className="ms-2 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                        {t("finance.expenses.monthlyTag")}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-gray-500">
                    {categoryLabel(expense.category)} ·{" "}
                    {formatDate(expense.occurred_on)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {expense.receipt_url && (
                    <a
                      href={`${
                        process.env.REACT_APP_API_URL ||
                        "http://localhost:3010/api"
                      }${expense.receipt_url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-primary-600 hover:underline"
                    >
                      {t("finance.expenses.receipt")}
                    </a>
                  )}
                  <span className="font-semibold text-gray-900 whitespace-nowrap">
                    {money(expense.amount)}
                  </span>
                  {can("expenses.delete") && expense.paid_from !== "drawer" && (
                    <button
                      onClick={() => remove(expense)}
                      className="text-sm text-red-600 hover:underline"
                    >
                      {t("common.remove")}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Expenses;
